import { useState, useEffect, useRef } from "react";

const PING_TYPE = "FINDIGO_EXTENSION_PING";
const PONG_TYPE = "FINDIGO_EXTENSION_PONG";
const PING_INTERVAL_MS = 400;
const PING_ATTEMPTS = 4;
const FINAL_TIMEOUT_MS = PING_INTERVAL_MS * PING_ATTEMPTS + 400; // ~2000ms total (EXT-BANNER-001: content script may inject after first PING)

/**
 * Detects if the Findigo browser extension is installed and responding.
 *
 * Uses postMessage PING/PONG to communicate with the extension's content script.
 * Sends PING repeatedly so we still detect the extension if the content script
 * injects after the first PING (e.g. document_end on findigo.se).
 *
 * Returns:
 * - null: checking (initial state)
 * - true: extension is installed and responding
 * - false: extension not detected (timeout or no response)
 */
export function useExtensionInstalled(): boolean | null {
  const [installed, setInstalled] = useState<boolean | null>(null);
  const resolved = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setInstalled(false);
      return;
    }

    resolved.current = false;
    setInstalled(null);

    const requestId = `install-check-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    const onMessage = (event: MessageEvent) => {
      if (resolved.current) return;
      if (event.data?.type !== PONG_TYPE || event.data?.requestId !== requestId) return;
      resolved.current = true;
      setInstalled(event.data?.installed === true);
      window.removeEventListener("message", onMessage);
    };

    window.addEventListener("message", onMessage);

    const sendPing = () => {
      if (resolved.current) return;
      window.postMessage({ type: PING_TYPE, requestId }, "*");
    };

    sendPing();
    const pingInterval = window.setInterval(sendPing, PING_INTERVAL_MS);

    const finalTimeout = window.setTimeout(() => {
      if (resolved.current) return;
      resolved.current = true;
      setInstalled(false);
      window.removeEventListener("message", onMessage);
    }, FINAL_TIMEOUT_MS);

    return () => {
      window.clearInterval(pingInterval);
      window.clearTimeout(finalTimeout);
      window.removeEventListener("message", onMessage);
    };
  }, []);

  return installed;
}
